import {
	CalendarDots,
	ChartLineUp,
	ChartPieSlice,
	Files,
	Folders,
	House,
	Invoice,
	ReadCvLogo,
	Receipt,
	SignOut,
	SquaresFour,
	UserCircle,
} from '@phosphor-icons/react';
import ROUTES, { ROUTES_SLUGS } from '../../../shared/constants/routes';
import { useNavigate } from 'react-router-dom';
import React, { useEffect, useRef, useState } from 'react';
import { useStorage } from '../../../shared/utils/general.util';
import { RootState } from '../../../redux/store';
import { useSelector, useDispatch } from 'react-redux';
import { logout, removeToken } from '../../../redux/slices/auth.slice';
import { restartAppState } from '../../../redux/slices/sat.slice';
import { restartTaxPayerData } from '../../../redux/slices/taxProfile.slice';
import LOCAL_STORAGE_KEY from '../../../shared/constants/localStorageKey';
import SESSION_STORAGE_KEY from '../../../shared/constants/sessionStorageKey';
import { PlatformOS } from '../../../redux/slices/profile.slice';
import STORAGE_KEYS from '../../../shared/constants/storage';
import RappiLogo from '../../../assets/img/svg/rappi-logo.svg';
import RappiLogoWhite from '../../../assets/img/svg/rappi-logo-white.svg';
import ZubaleLogo from '../../../assets/img/png/zubaleLogo.png';

interface Option {
	label: string | null;
	slug: string;
	icon: React.ReactNode;
	path: string;
	notificationIcon?: React.ReactNode;
}

const plansOption = {
	label: 'Planes',
	slug: ROUTES_SLUGS.PLANS,
	icon: (
		<Files
			size={24}
			weight='bold'
		/>
	),
	path: ROUTES.PLANS_AND_PRICES,
};
const invoiceOption = {
	label: 'Facturación',
	slug: ROUTES_SLUGS.INVOICING,
	icon: (
		<Receipt
			size={24}
			weight='bold'
		/>
	),
	path: ROUTES.INVOICING,
};
const homeOption = {
	label: 'Inicio',
	slug: ROUTES_SLUGS.HOME,
	icon: (
		<House
			size={24}
			weight='bold'
		/>
	),
	path: ROUTES.ROOT,
};
const taxOption = {
	label: 'Declaraciones',
	slug: ROUTES_SLUGS.TAXES,
	icon: (
		<ChartLineUp
			size={24}
			weight='bold'
		/>
	),
	path: ROUTES.TAXES,
};

const annualOption = {
	label: 'Declaración anual 2024',
	slug: ROUTES_SLUGS.ANNUAL_DECLARATION,
	icon: (
		<Folders
			size={24}
			weight='bold'
		/>
	),
	path: ROUTES.ANNUAL_DECLARATION,
};

const submenuOption = {
	label: null,
	slug: ROUTES_SLUGS.MORE,
	icon: (
		<SquaresFour
			size={24}
			weight='bold'
		/>
	),
	path: 'submenu',
};
const logoutOption = {
	label: 'Cerrar sesión',
	slug: ROUTES_SLUGS.LOGOUT,
	icon: <SignOut size={24} />,
	path: ROUTES_SLUGS.LOGOUT,
};
const purchasesOption = {
	label: 'Estado de tus compras',
	slug: ROUTES_SLUGS.SERVICE_STATUS,
	icon: <ChartPieSlice size={24} />,
	path: ROUTES.SERVICE_STATUS,
};
const invoicesOption = {
	label: 'Tus facturas en el SAT',
	slug: ROUTES_SLUGS.FISCAL_DOCUMENTS,
	icon: <ReadCvLogo size={24} />,
	path: ROUTES.FISCAL_DOCUMENTS,
};
const profileOption = {
	label: `Tu perfil heru`,
	slug: ROUTES_SLUGS.USER_PROFILE,
	icon: <UserCircle size={24} />,
	path: ROUTES.USER_PROFILE,
};
const imssRegulationsOption = {
	label: 'IMSS',
	slug: ROUTES_SLUGS.NEW_IMSS_REGULATIONS,
	icon: <CalendarDots size={24} />,
	path: ROUTES.NEW_IMS_REGULATIONS,
};

// ================================ Certificates options ================================ //
const rappiOption = {
	label: 'Certificados Rappi',
	slug: ROUTES_SLUGS.RAPPI_CERTIFICATES,
	icon: (
		<img
			src={
				location.pathname.includes(ROUTES_SLUGS.RAPPI_CERTIFICATES)
					? RappiLogoWhite
					: RappiLogo
			}
			alt='Rappi'
			className='w-10 h-10'
		/>
	),
	path: ROUTES.RAPPI_CERTIFICATES,
};
const zubaleOption = {
	label: 'Certificados Zúbale',
	slug: ROUTES_SLUGS.ZUBALE_CERTIFICATES,
	icon: (
		<img
			src={ZubaleLogo}
			alt='Zubale'
			style={{ width: '22px', height: '22px' }}
		/>
	),
	path: ROUTES.ZUBALE_CERTIFICATES,
};
const generalCertificatesOption = {
	label: 'Certificados',
	slug: ROUTES_SLUGS.GENERAL_CERTIFICATES,
	icon: <Invoice size={20} />,
	path: ROUTES.GENERAL_CERTIFICATES,
};

// ================================ Default options ================================ //
const defaultMenuOptions = {
	menu: [plansOption, invoiceOption, homeOption, taxOption, submenuOption],
	submenu: [
		logoutOption,
		annualOption,
		purchasesOption,
		invoicesOption,
		profileOption,
	],
};

const imssMenuOptions = {
	menu: [
		plansOption,
		invoiceOption,
		homeOption,
		imssRegulationsOption,
		submenuOption,
	],
	submenu: [
		logoutOption,
		taxOption,
		annualOption,
		purchasesOption,
		invoicesOption,
		profileOption,
	],
};

const MobileMenu = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const isFromIOS =
		useStorage().getItem(STORAGE_KEYS.PLATFORM_OS) === PlatformOS.IOS;
	const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
	const subMenuRef = useRef<HTMLDivElement>(null);
	const rappiCertificates = useSelector(
		(state: RootState) => state.rappi.certificates,
	);
	const zubaleCertificates = useSelector(
		(state: RootState) => state.zubale.certificates,
	);
	const { tags } = useSelector((state: RootState) => state.profile);

	const [options, setOptions] = useState<{
		menu: Option[];
		submenu: Option[];
	}>(defaultMenuOptions);

	const handleLogout = () => {
		useStorage().removeItem(LOCAL_STORAGE_KEY.TOKEN);
		useStorage().removeItem(SESSION_STORAGE_KEY.SHOW_NO_INFO_MODAL);
		dispatch(removeToken());
		dispatch(restartAppState());
		dispatch(restartTaxPayerData());
		dispatch(logout());
		navigate(ROUTES.LOGIN);
	};

	const handleNavigate = (path: string) => {
		if (path === 'submenu') {
			setIsSubMenuOpen((prev) => !prev);
		} else if (path === ROUTES_SLUGS.LOGOUT) {
			handleLogout();
		} else {
			setIsSubMenuOpen(false);
			navigate(path);
		}
	};

	useEffect(() => {
		let baseOptions = tags?.some((tag) => tag.tag === 'new-imss-regulations')
			? { ...imssMenuOptions }
			: { ...defaultMenuOptions };

		let certificatesOptionToAdd = null;

		// HAVE BOTH CERTIFICATES
		if (zubaleCertificates.length && rappiCertificates.length) {
			certificatesOptionToAdd = generalCertificatesOption;
		}
		// HAVE ONLY ZUBALE CERTIFICATES
		else if (zubaleCertificates.length) {
			certificatesOptionToAdd = zubaleOption;
		}
		// HAVE ONLY RAPPI CERTIFICATES
		else if (rappiCertificates?.length) {
			certificatesOptionToAdd = rappiOption;
		}

		if (
			certificatesOptionToAdd &&
			!baseOptions.submenu.some(
				(option) => option.slug === certificatesOptionToAdd.slug,
			)
		) {
			baseOptions.submenu = [
				baseOptions.submenu[0],
				certificatesOptionToAdd,
				...baseOptions.submenu.slice(1),
			];
		}

		setOptions(baseOptions);
	}, [tags, zubaleCertificates, rappiCertificates]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				subMenuRef.current &&
				!subMenuRef.current.contains(event.target as Node)
			) {
				setIsSubMenuOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	return (
		<div
			ref={subMenuRef}
			className={`z-[100] w-full bg-white/80 backdrop-blur-lg flex border-t border-h-neutral-90 md:hidden fixed bottom-0 gap-1 md:relative p-0.5 ${
				isFromIOS ? 'pb-8' : ''
			}`}
		>
			{isSubMenuOpen && (
				<div
					className={`z-[100] fixed right-2 bg-white rounded-xl shadow-lg w-[220px] border border-h-neutral-90 ${
						isFromIOS ? 'bottom-[100px]' : 'bottom-[60px]'
					}`}
				>
					{options.submenu.map((option, index) => {
						const isActive = location.pathname === option.path;
						const icon = React.cloneElement(option.icon as React.ReactElement, {
							weight: isActive ? 'fill' : 'light',
						});

						return (
							<div
								onClick={() => handleNavigate(option.path)}
								className={`flex items-center justify-end gap-3 text-sm py-3.5 px-4 text-right font-medium border-b border-h-neutral-90
									${isActive ? 'bg-h-primary text-white' : ''}`}
								key={`${option.slug}-${index}`}
							>
								{option.label} {icon}
							</div>
						);
					})}
				</div>
			)}

			{options.menu.map((option) => {
				const isSubMenuActive =
					isSubMenuOpen && option.slug === ROUTES_SLUGS.MORE;
				const isActive = location.pathname === option.path;

				const isActiveSubmenu =
					option.path === 'submenu' &&
					[
						ROUTES.SERVICE_STATUS,
						ROUTES.FISCAL_DOCUMENTS,
						ROUTES.USER_PROFILE,
					].includes(location.pathname);

				const icon = React.cloneElement(option.icon as React.ReactElement, {
					weight:
						isActive || isSubMenuActive || isActiveSubmenu
							? 'duotone'
							: 'regular',
				});

				return (
					<div
						onClick={() => handleNavigate(option.path)}
						className={`w-full flex flex-col items-center justify-center p-1 text-[10px] font-medium text-h-dark rounded-lg relative
							${isActive ? 'bg-h-primary-95 text-h-primary !font-bold' : ''}
							${isSubMenuActive ? 'bg-h-primary-95 text-h-primary' : ''}
							${isActiveSubmenu ? '!bg-h-primary-90 !text-h-primary' : ''}`}
						key={`option-${option.slug}`}
					>
						{icon}
						{option.label}
						{option.notificationIcon}
					</div>
				);
			})}
		</div>
	);
};

export default MobileMenu;
